import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

interface SendSuccessOverlayProps {
  message: string;
  onComplete: () => void;
  durationMs?: number;
}

/**
 * Full-screen animated success overlay shown after sending a message.
 * Auto-fires onComplete after durationMs (default 6000).
 */
export function SendSuccessOverlay({ message, onComplete, durationMs = 6000 }: SendSuccessOverlayProps) {
  useEffect(() => {
    const timer = setTimeout(onComplete, durationMs);
    return () => clearTimeout(timer);
  }, [onComplete, durationMs]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
      >
        <CheckCircle2 className="w-32 h-32 text-green-500" strokeWidth={1.5} />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="mt-8 text-3xl font-bold text-foreground text-center px-6"
      >
        {message}
      </motion.p>

    </motion.div>
  );
}
